import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, X, Edit, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrivacyZonesPage = () => {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newZone, setNewZone] = useState({
    name: '',
    latitude: '',
    longitude: '',
    radius: '500'
  });

  useEffect(() => {
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API}/privacy-zones`);
      setZones(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/privacy-zones`, newZone);
      setShowAdd(false);
      setNewZone({ name: '', latitude: '', longitude: '', radius: '500' });
      fetchZones();
    } catch (error) {
      console.error('Error adding zone:', error);
    }
  };

  const handleDelete = async (zoneId) => {
    if (!confirm('Delete this privacy zone?')) return;
    try {
      await axios.delete(`${API}/privacy-zones/${zoneId}`);
      fetchZones();
    } catch (error) {
      console.error('Error deleting zone:', error);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C8A878', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="page-title">Privacy Zones</h1>
      </div>

      <div style={{ padding: '20px' }}>
        <div style={{ background: '#3A2A1E', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #C8A878' }}>
          <p style={{ fontSize: '14px', color: '#F1E6D0', lineHeight: '1.6' }}>
            Privacy zones hide your exact location when you're in specific areas like home or work. Your location will appear as "Nearby" instead of showing precise distance.
          </p>
        </div>

        <button
          onClick={() => setShowAdd(true)}
          className="btn-primary"
          style={{ width: '100%', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
          data-testid="add-privacy-zone-btn"
        >
          <Plus size={20} />
          Add Privacy Zone
        </button>

        {zones.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#C8A878' }}>
            <p>No privacy zones yet</p>
            <p style={{ fontSize: '14px', marginTop: '8px' }}>Tap "Add Privacy Zone" to create one</p>
          </div>
        ) : (
          zones.map((zone) => (
            <div key={zone.id} className="card" style={{ marginBottom: '12px' }} data-testid={`zone-${zone.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>{zone.name}</h3>
                  <p style={{ fontSize: '14px', color: '#F1E6D0' }}>Radius: {zone.radius}m</p>
                </div>
                <button
                  onClick={() => handleDelete(zone.id)}
                  style={{ background: 'none', border: 'none', color: '#7A1E1E', cursor: 'pointer', padding: '8px' }}
                  data-testid={`delete-zone-${zone.id}`}
                >
                  <Trash2 size={20} />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Add Privacy Zone</h2>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', color: '#F1E6D0', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>Zone Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g., Home, Work, Gym"
                  value={newZone.name}
                  onChange={(e) => setNewZone({ ...newZone, name: e.target.value })}
                  data-testid="zone-name-input"
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>Latitude</label>
                <input
                  type="number"
                  step="any"
                  className="input-field"
                  placeholder="25.7617"
                  value={newZone.latitude}
                  onChange={(e) => setNewZone({ ...newZone, latitude: e.target.value })}
                  data-testid="zone-lat-input"
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>Longitude</label>
                <input
                  type="number"
                  step="any"
                  className="input-field"
                  placeholder="-80.1918"
                  value={newZone.longitude}
                  onChange={(e) => setNewZone({ ...newZone, longitude: e.target.value })}
                  data-testid="zone-lng-input"
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '600' }}>Radius (meters)</label>
                <input
                  type="number"
                  className="input-field"
                  value={newZone.radius}
                  onChange={(e) => setNewZone({ ...newZone, radius: e.target.value })}
                  data-testid="zone-radius-input"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleAdd} data-testid="save-zone-btn">Add Zone</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyZonesPage;
